/* eslint-disable react/forbid-prop-types */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Paper from '@base/Paper/Paper';
import P1 from '@base/P1/P1';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import Link from 'next/link';
import Tooltip from '@base/Tooltip/Tooltip';
import { pluralize } from '@utils/textHelper';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { PRACTICE_MIN_QUESTION_NUM } from '../constants';

const Card = styled(Paper)`
  border-left: ${({ $color }) =>
    `8px solid ${$color || 'var(--color-divider)'}`};
  &.paper {
    padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_8B}
      ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  }
  grid-column: span 6;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    padding: ${Spacings.SPACING_6B};
  }
`;

const CardTitle = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const CardDescription = styled(P1)`
  margin-top: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const Icon = styled.img`
  margin-right: ${Spacings.SPACING_4B};
`;

const TooltipContent = styled.div`
  font-size: ${Fonts.P1};
  font-weight: ${Fonts.SEMIBOLD};
  height: 24px;
  display: grid;
  place-items: center;
`;

const TopicCard = ({
  topic,
  href,
  asLink,
  displayConcepts,
  displayIcon,
  className,
  color,
  onClick,
  isGTP
}) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const isDisabled =
    isGTP &&
    isLoggedIn &&
    topic?.free_question_count < PRACTICE_MIN_QUESTION_NUM;

  const getCardDescription = () => {
    const questionCountInfo = pluralize(
      topic?.total_question_count,
      'question'
    );

    const availableQuestionInfo = `${topic?.free_question_count} of ${pluralize(
      topic?.total_question_count,
      'question'
    )} available`;

    if (isLoggedIn) {
      if (isDisabled) {
        return questionCountInfo;
      }
      return availableQuestionInfo;
    }

    return questionCountInfo;
  };

  const renderCard = (cardOnClick) => {
    return (
      <Card
        className={`${className} paper`}
        $color={isDisabled ? 'var(--color-text-secondary)' : color}
        $disabled={isDisabled}
        onClick={cardOnClick}
      >
        {displayIcon && (
          <Icon src={displayIcon} alt={topic.name} width="44" height="44" />
        )}
        <CardTitle
          color="var(--color-text-primary)"
          lineClamp={displayConcepts ? 1 : 2}
        >
          {topic.name}
        </CardTitle>
        {/* {displayConcepts && (
      <CardDescription>{`${
        topic.concepts_count ?? 0
      } concepts`}</CardDescription>
    )} */}
        {isGTP && <CardDescription>{getCardDescription()}</CardDescription>}
      </Card>
    );
  };

  if (isDisabled) {
    return (
      <Tooltip
        padding={Spacings.SPACING_4B}
        maxWidth="330px"
        title={
          <TooltipContent>
            Get subscription to access practice questions
          </TooltipContent>
        }
        open={isDisabled ? null : false}
        borderRadius={Spacings.SPACING_2B}
      >
        {renderCard()}
      </Tooltip>
    );
  }

  if (isGTP) {
    return renderCard(onClick);
  }

  return (
    <Link href={href} as={asLink} passHref onClick={onClick}>
      {renderCard()}
    </Link>
  );
};

TopicCard.propTypes = {
  topic: PropTypes.objectOf(PropTypes.any).isRequired,
  href: PropTypes.string.isRequired,
  asLink: PropTypes.string.isRequired,
  displayConcepts: PropTypes.bool,
  className: PropTypes.string,
  displayIcon: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  isGTP: PropTypes.bool
};

TopicCard.defaultProps = {
  displayConcepts: true,
  className: '',
  displayIcon: '',
  onClick: () => {},
  color: 'var(--color-divider)',
  isGTP: false
};

export default TopicCard;
